/**
 * Per-line “Ask for credit?” intent for Goods Receipting.
 * Reason picks a sensible default; the checkbox always wins.
 *
 * Principle: GR records what physically happened. Financial credit is only
 * certain once the invoice arrives — so physical-only reasons (e.g. Not
 * Delivered) default Ask for credit OFF. Damaged / quality still default ON
 * because the kitchen already knows goods are unfit to pay for.
 */

export const GR_CREDIT_DEFAULT_BY_REASON = {
  Damaged: true,
  'Quality Issue': true,
  // Outstanding physically ≠ financially owed until the invoice arrives.
  'Not Delivered': false,
  'Something Else': true,
  Substitution: true,
  'Supplier / Invoice adjustment': false,
  'Weighted item': false,
  'Read Only': false,
}

/** Default Ask for credit? for a line reason (Weighted / invoice adjust → off). */
export function getDefaultAskForCredit(errorReason) {
  const reason = String(errorReason || '').trim()
  if (!reason) return false
  if (
    Object.prototype.hasOwnProperty.call(GR_CREDIT_DEFAULT_BY_REASON, reason)
  ) {
    return GR_CREDIT_DEFAULT_BY_REASON[reason]
  }
  // Unknown reasons default to credit request (safer for kitchen “problems”).
  return true
}

/** Line status stamped for save — checkbox wins over reason. */
export function getReceiptingLineStatusForCredit(askForCredit) {
  return askForCredit ? 'Sent to supplier' : 'Accepted'
}

/**
 * Resolve askForCredit on a draft line (checkbox explicit, else default by reason).
 */
export function resolveAskForCredit(line = {}) {
  if (typeof line.askForCredit === 'boolean') {
    return line.askForCredit
  }
  return getDefaultAskForCredit(line.error)
}

/**
 * Whether this PO’s draft lines include at least one credit request.
 */
export function hasAskForCreditLines(record = {}, editedReceipting = []) {
  const supplierId = record?.supplier?._id || record?.supplier
  const po = record?.purchaseOrderNumber
  if (!supplierId || !po) return false

  return (editedReceipting || []).some(item => {
    if (item?.purchaseOrderNumber !== po) return false
    if (item.supplier !== supplierId && item.supplier?._id !== supplierId) {
      return false
    }
    if (!String(item.error || '').trim()) return false
    return resolveAskForCredit(item) === true
  })
}

/**
 * Count draft lines with Ask for credit? for this PO.
 */
export function countAskForCreditLines(record = {}, editedReceipting = []) {
  const supplierId = record?.supplier?._id || record?.supplier
  const po = record?.purchaseOrderNumber
  if (!supplierId || !po) return 0

  return (editedReceipting || []).filter(item => {
    if (item?.purchaseOrderNumber !== po) return false
    if (item.supplier !== supplierId && item.supplier?._id !== supplierId) {
      return false
    }
    if (!String(item.error || '').trim()) return false
    return resolveAskForCredit(item) === true
  }).length
}

/**
 * Save-receipt confirm copy + which backend saveType to use.
 * CN email path only when at least one line asks for credit.
 */
export function getSaveReceiptConfirmMeta(record = {}, editedReceipting = []) {
  const creditLineCount = countAskForCreditLines(record, editedReceipting)
  const willRequestCredit = creditLineCount > 0
  return {
    creditLineCount,
    willRequestCredit,
    saveType: willRequestCredit ? 'cn_request' : 'reconciliation',
    title: 'Save receipt?',
    okText: 'Save receipt',
    description: willRequestCredit
      ? `This marks the order as receipted. A credit request email will be sent for ${creditLineCount} line${
          creditLineCount === 1 ? '' : 's'
        }. You can match an invoice next.`
      : 'This marks the order as receipted with your line changes. No credit request email will be sent. You can match an invoice next.',
  }
}
